import React from 'react';
import Icon from '../../../components/AppIcon';

const LiveActivityFeed = ({ activities, isProcessing }) => {
  const getActivityIcon = (type) => {
    switch (type) {
      case 'exact_match':
        return 'CheckCircle2';
      case 'fuzzy_match':
        return 'Target';
      case 'no_match':
        return 'XCircle';
      case 'ambiguous':
        return 'AlertTriangle';
      case 'error':
        return 'AlertCircle';
      default:
        return 'Circle';
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'exact_match':
        return 'text-success';
      case 'fuzzy_match':
        return 'text-primary';
      case 'no_match':
        return 'text-muted-foreground';
      case 'ambiguous':
        return 'text-warning';
      case 'error':
        return 'text-error';
      default:
        return 'text-muted-foreground';
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.95) return 'text-success';
    if (confidence >= 0.85) return 'text-primary';
    if (confidence >= 0.75) return 'text-warning';
    return 'text-error';
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp)?.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">Live Activity Feed</h3>
          <div className="flex items-center space-x-2">
            {isProcessing && (
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
                <span className="text-sm text-success font-medium">Live</span>
              </div>
            )}
            <Icon name="Activity" size={16} className="text-muted-foreground" />
          </div>
        </div>
      </div>
      <div className="max-h-96 overflow-y-auto">
        {activities?.length === 0 ? (
          <div className="p-8 text-center">
            <Icon name="Clock" size={32} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">
              {isProcessing ? 'Waiting for processing to begin...' : 'No activity to display'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {activities?.map((activity, index) => (
              <div key={activity?.id || index} className="p-4 hover:bg-muted transition-colors duration-150">
                <div className="flex items-start space-x-3">
                  <Icon 
                    name={getActivityIcon(activity?.type)} 
                    size={16} 
                    className={`mt-1 ${getActivityColor(activity?.type)}`}
                  />
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-foreground truncate">
                        {activity?.productName}
                      </span>
                      <span className="text-xs text-muted-foreground ml-2">
                        {formatTimestamp(activity?.timestamp)}
                      </span>
                    </div>

                    <div className="text-sm text-muted-foreground mb-2">
                      {activity?.type === 'exact_match' && (
                        <span>Exact match found via {activity?.matchMethod}</span>
                      )}
                      {activity?.type === 'fuzzy_match' && (
                        <span>Fuzzy match found with {activity?.matchedProduct}</span>
                      )}
                      {activity?.type === 'no_match' && (
                        <span>No matching products found</span>
                      )}
                      {activity?.type === 'ambiguous' && (
                        <span>Multiple potential matches require review</span>
                      )}
                      {activity?.type === 'error' && (
                        <span>Processing error: {activity?.error}</span>
                      )}
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        {activity?.confidence !== undefined && (
                          <div className="flex items-center space-x-1">
                            <span className="text-xs text-muted-foreground">Confidence:</span>
                            <span className={`text-xs font-medium ${getConfidenceColor(activity?.confidence)}`}>
                              {(activity?.confidence * 100)?.toFixed(1)}%
                            </span>
                          </div>
                        )}

                        {activity?.supplier && (
                          <div className="flex items-center space-x-1">
                            <span className="text-xs text-muted-foreground">Supplier:</span>
                            <span className="text-xs font-medium text-foreground">
                              {activity?.supplier}
                            </span>
                          </div>
                        )}
                      </div>

                      {activity?.price && (
                        <div className="text-sm font-semibold text-foreground">
                          ${activity?.price?.toFixed(2)}
                        </div>
                      )}
                    </div>

                    {activity?.details && (
                      <div className="mt-2 text-xs text-muted-foreground bg-muted p-2 rounded">
                        {activity?.details}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {activities?.length > 0 && (
        <div className="p-3 border-t border-border bg-muted">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Showing latest {activities?.length} activities</span>
            <button className="text-primary hover:text-primary-foreground transition-colors">
              View all activity
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveActivityFeed;